export type TarFile = { path: string; data: Uint8Array };

const text = (b: Uint8Array) => new TextDecoder().decode(b.subarray(0, b.indexOf(0) < 0 ? b.length : b.indexOf(0)));
const octal = (b: Uint8Array) => parseInt(text(b).trim() || "0", 8);

/** Regular files of a (ustar, GNU or PAX) tar archive; `want` filters by path before copying data. */
export function untar(buf: Uint8Array, want: (path: string) => boolean = () => true): TarFile[] {
  const out: TarFile[] = [];
  let longName: string | null = null;
  for (let off = 0; off + 512 <= buf.length; ) {
    const h = buf.subarray(off, off + 512);
    if (h.every((x) => x === 0)) break;
    const size = octal(h.subarray(124, 136));
    if (!Number.isFinite(size) || size < 0 || off + 512 + size > buf.length) break;
    const type = String.fromCharCode(h[156] || 48);
    const body = buf.subarray(off + 512, off + 512 + size);
    off += 512 + Math.ceil(size / 512) * 512;
    if (type === "L") {
      longName = text(body);
      continue;
    }
    if (type === "x") {
      const m = /(?:^|\n)\d+ path=([^\n]*)\n/.exec(new TextDecoder().decode(body));
      if (m) longName = m[1];
      continue;
    }
    const prefix = text(h.subarray(345, 500));
    const name = longName ?? (prefix ? `${prefix}/${text(h.subarray(0, 100))}` : text(h.subarray(0, 100)));
    longName = null;
    if ((type === "0" || type === "\0") && want(name.replace(/^\.\//, ""))) out.push({ path: name.replace(/^\.\//, ""), data: body.slice() });
  }
  return out;
}

/** Decompresses gzip, failing once the output passes `limit` bytes (artifacts are untrusted). */
export async function gunzip(bytes: Uint8Array, limit = 200 * 1024 * 1024): Promise<Uint8Array> {
  const reader = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream("gzip")).getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.length;
    if (total > limit) {
      await reader.cancel();
      throw new Error(`archive expands past ${limit} bytes`);
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

export type ShotPair = { name: string; before?: TarFile; after?: TarFile };

const IMAGE = /\.(png|jpe?g|webp|gif)$/i;

/**
 * Pairs screenshots by name: `before/x.png` with `after/x.png`, or `x.before.png` with `x.after.png`.
 * Images with no before/after marker are shown on their own as "after".
 */
export function pairShots(files: TarFile[]): ShotPair[] {
  const pairs = new Map<string, ShotPair>();
  for (const f of files) {
    if (!IMAGE.test(f.path)) continue;
    const m = /(?:^|\/)(before|after)\/(.+)$/.exec(f.path) ?? /^(?:.*\/)?(.+?)[.-](before|after)(\.[a-z]+)$/i.exec(f.path);
    let side: "before" | "after" = "after";
    let name = f.path.replace(/^.*?(?:screenshots|shots)\//, "");
    if (m && (m[1] === "before" || m[1] === "after")) {
      side = m[1];
      name = m[2];
    } else if (m) {
      side = m[2].toLowerCase() as "before" | "after";
      name = m[1] + m[3];
    }
    const p = pairs.get(name) ?? { name };
    p[side] = f;
    pairs.set(name, p);
  }
  return [...pairs.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export const isShotPath = (p: string) => IMAGE.test(p) && /(^|\/)(screenshots|shots)\//.test(p);
